// frontend/src/widgets/ContactFormWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-08
// Updated:      2026-09-07 15:49 BST
//
// Changes (2026-09-07):
//   • Added mobile responsiveness while preserving the existing desktop two-column form.
//   • Mobile uses a single-column form with touch-safe controls and full-width actions.
//   • Mobile widget host height follows the natural form height because mobile widgets
//     do not expose the desktop ReactGridLayout resize handle.
//   • No create/edit payload, username-generation, endpoint fallback, or portal-access
//     behaviour was changed during this responsiveness pass.
//
// Changes (2025-10-07):
//   • Uses shared makePortalUsername() utility (first3+first3+NNN) for portal accounts.
//   • Preloads /api/users to ensure uniqueness; sends username, username_suggestion, portal_username.
//   • Keeps nested-route fallbacks and all prior fields/UX intact.
//
// Operational Notes:
//   • This file still uses direct axios calls, a locally resolved API base, username preloading,
//     and compatibility endpoint fallbacks. Those are existing technical-debt items and were
//     deliberately left unchanged during the mobile conversion.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { makePortalUsername } from "../utils/username";

/**
 * Resolve API base (no trailing slash).
 */
const API_BASE = (() => {
  const envBase = import.meta?.env?.VITE_API_BASE;
  if (envBase) return String(envBase).replace(/\/+$/, "");
  const url = new URL(window.location.href);
  const { protocol, hostname, port } = url;
  if (port === "8010" || port === "8070") return `${protocol}//${hostname}:8020/api`;
  if (port === "8020") return `${protocol}//${hostname}:8020/api`;
  return `${url.origin}/api`;
})();

const MOBILE_BREAKPOINT = 768;

const API = {
  customers: `${API_BASE}/customers`,
  contacts: `${API_BASE}/contacts`,
  customerContacts: (id) => `${API_BASE}/customers/${id}/contacts`,
  users: `${API_BASE}/users`,
};

const defaultContact = {
  // Core
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  status: "Active",
  role: "Client",
  access_level: "",
  notes: "",
  portal_access: true,
  is_primary: true,
  customer_id: "",

  // Extended
  title: "",
  department: "",
  job_title: "",
  secondary_email: "",
  mobile_number: "",
  direct_line: "",
  extension: "",
  whatsapp: "",
  preferred_contact_method: "",

  // Flags
  is_billing_contact: false,
  is_technical_contact: false,
};

// letters-only helpers for UI-side base display if needed
const onlyLetters = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z]/g, "");
const pad3 = (s) => (s + "xxx").slice(0, 3);

const ContactFormWidget = ({ contact = null, onClose = null }) => {
  const rootRef = useRef(null);

  const [form, setForm] = useState(contact || defaultContact);
  const [companies, setCompanies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const [existingUsernames, setExistingUsernames] = useState(new Set());
  const isEdit = Boolean(contact);

  const authConfig = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${localStorage.getItem("anpr_token")}` },
    }),
    []
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (window.innerWidth >= MOBILE_BREAKPOINT) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Companies
    axios
      .get(`${API.customers}?limit=1000`, authConfig)
      .then((res) => {
        if (cancelled) return;

        const active = (res.data || [])
          .filter((c) => c.status === "Active")
          .sort((a, b) =>
            String(a?.name || a?.company_name || a?.title || "").localeCompare(
              String(b?.name || b?.company_name || b?.title || ""),
              undefined,
              { sensitivity: "base", numeric: true }
            )
          );

        setCompanies(active);
      })
      .catch((err) => {
        console.error("❌ Failed to load companies:", err?.response?.data || err?.message || err);
      });

    // Existing usernames for uniqueness (create only)
    if (!isEdit) {
      axios
        .get(`${API.users}?limit=2000`, authConfig)
        .then((res) => {
          if (cancelled) return;
          const arr = Array.isArray(res.data) ? res.data : [];
          const set = new Set(arr.map((u) => String(u?.username || "").toLowerCase()).filter(Boolean));
          setExistingUsernames(set);
        })
        .catch((err) => {
          console.warn("⚠️ Failed to preload usernames:", err?.response?.data || err?.message || err);
          setExistingUsernames(new Set());
        });
    }

    if (isEdit) {
      setForm((prev) => ({ ...defaultContact, ...contact }));
      console.info("✅ Loaded contact for edit:", contact?.id);
    }

    return () => {
      cancelled = true;
    };
  }, [isEdit, contact, authConfig]);

  const toIntOrNull = (v) => {
    if (v === "" || v === null || typeof v === "undefined") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numericFields = new Set(["customer_id"]);
    const booleanFields = new Set(["portal_access", "is_primary", "is_billing_contact", "is_technical_contact"]);
    let finalValue;
    if (booleanFields.has(name)) finalValue = !!checked;
    else if (numericFields.has(name)) finalValue = value === "" ? "" : Number(value);
    else finalValue = value;

    setForm((prev) => ({ ...prev, [name]: finalValue }));
  };

  const buildPayload = () => {
    const payload = { ...form, customer_id: toIntOrNull(form.customer_id) };

    // Portal username (create only)
    if (!isEdit && payload.portal_access) {
      const unique = makePortalUsername(payload.first_name, payload.last_name, payload.email, existingUsernames);
      payload.username = unique;
      payload.username_suggestion = unique;
      payload.portal_username = unique;
      // optimistic add to prevent duplicates during rapid entry
      setExistingUsernames((prev) => new Set(prev).add(unique));
    }

    // Prune empties
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") delete payload[k];
    });

    return payload;
  };

  // HTTP helpers (unchanged)
  const tryPost = async (url, payload) => {
    try {
      const res = await axios.post(url, payload, authConfig);
      return res;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 405 || status === 307 || status === 308) return null;
      throw err;
    }
  };

  const tryPut = async (url, payload) => {
    try {
      const res = await axios.put(url, payload, authConfig);
      return res;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 405 || status === 307 || status === 308) return null;
      throw err;
    }
  };

  const createContactWithFallbacks = async (payload) => {
    if (payload.customer_id) {
      const nested = await tryPost(API.customerContacts(payload.customer_id), payload);
      if (nested) return nested;
      const nestedSlash = await tryPost(API.customerContacts(payload.customer_id) + "/", payload);
      if (nestedSlash) return nestedSlash;
    }
    const slash = await tryPost(API.contacts + "/", payload);
    if (slash) return slash;
    const noslash = await tryPost(API.contacts, payload);
    if (noslash) return noslash;

    const err = new Error("No compatible create endpoint found. Tried /customers/{id}/contacts, /contacts/, /contacts.");
    err.code = "NO_CREATE_ENDPOINT";
    throw err;
  };

  const updateContactWithFallbacks = async (id, payload) => {
    const noSlash = await tryPut(`${API.contacts}/${id}`, payload);
    if (noSlash) return noSlash;
    const withSlash = await tryPut(`${API.contacts}/${id}/`, payload);
    if (withSlash) return withSlash;

    const err = new Error("No compatible update endpoint found. Tried /contacts/{id} and /contacts/{id}/.");
    err.code = "NO_UPDATE_ENDPOINT";
    throw err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);

    try {
      const payload = buildPayload();

      if (!payload.customer_id) {
        setMessage("Please select a company");
        setSubmitting(false);
        return;
      }

      if (isEdit) {
        await updateContactWithFallbacks(contact.id, payload);
        setMessage("Contact updated successfully");
        console.info("✅ Updated contact:", contact.id);
      } else {
        await createContactWithFallbacks(payload);
        setMessage("Contact created successfully");
        console.info("✅ Created contact");
        setForm(defaultContact);
      }

      if (onClose) onClose();
    } catch (err) {
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unknown error";
      console.error("❌ Failed to save contact:", err?.response?.data || err?.message || err);
      setMessage(status ? `Failed to save contact (HTTP ${status}): ${detail}` : `Failed to save contact: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      ref={rootRef}
      onSubmit={handleSubmit}
      className="grid h-auto w-full grid-cols-1 gap-3 overflow-visible text-sm text-gray-800 font-body md:h-full md:grid-cols-2 md:gap-4 md:overflow-y-auto"
    >
      {/* Identity */}
      <select
        name="title"
        value={form.title}
        onChange={handleChange}
        className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-base md:text-sm"
      >
        <option value="">Title</option>
        <option>Mr</option>
        <option>Ms</option>
        <option>Mrs</option>
        <option>Dr</option>
        <option>Prof</option>
      </select>

      <input
        type="text"
        name="first_name"
        value={form.first_name}
        onChange={handleChange}
        placeholder="First Name"
        required
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />
      <input
        type="text"
        name="last_name"
        value={form.last_name}
        onChange={handleChange}
        placeholder="Last Name"
        required
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />

      {/* Company */}
      <select
        name="customer_id"
        value={form.customer_id ?? ""}
        onChange={handleChange}
        required
        className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-base md:col-span-2 md:text-sm"
      >
        <option value="">Select Company</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      {/* Role & Access */}
      <select
        name="role"
        value={form.role}
        onChange={handleChange}
        className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-base md:text-sm"
      >
        <option value="Primary">Primary</option>
        <option value="Admin">Admin</option>
        <option value="Finance">Finance</option>
        <option value="Other">Other</option>
        <option value="Client">Client</option>
      </select>

      <select
        name="access_level"
        value={form.access_level}
        onChange={handleChange}
        className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-base md:text-sm"
      >
        <option value="">Select Access Level</option>
        <option value="Organisation Manager">Organisation Manager</option>
        <option value="Client User Manager">Client User Manager</option>
        <option value="Job Viewer">Job Viewer</option>
        <option value="Portal Access Manager">Portal Access Manager</option>
        <option value="Brand Manager">Brand Manager</option>
      </select>

      {/* Employment */}
      <input
        type="text"
        name="department"
        value={form.department}
        onChange={handleChange}
        placeholder="Department"
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />
      <input
        type="text"
        name="job_title"
        value={form.job_title}
        onChange={handleChange}
        placeholder="Job Title"
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />

      {/* Contact Methods */}
      <input
        type="email"
        name="email"
        value={form.email}
        onChange={handleChange}
        placeholder="Email Address"
        required
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />
      <input
        type="email"
        name="secondary_email"
        value={form.secondary_email}
        onChange={handleChange}
        placeholder="Secondary Email"
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />

      <input
        type="text"
        name="phone_number"
        value={form.phone_number}
        onChange={handleChange}
        placeholder="Main Phone"
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />
      <input
        type="text"
        name="mobile_number"
        value={form.mobile_number}
        onChange={handleChange}
        placeholder="Mobile"
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />

      <input
        type="text"
        name="direct_line"
        value={form.direct_line}
        onChange={handleChange}
        placeholder="Direct Line"
        className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[8rem_minmax(0,1fr)] md:gap-4">
        <input
          type="text"
          name="extension"
          value={form.extension}
          onChange={handleChange}
          placeholder="Ext."
          className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
        />
        <input
          type="text"
          name="whatsapp"
          value={form.whatsapp}
          onChange={handleChange}
          placeholder="WhatsApp"
          className="min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:text-sm"
        />
      </div>

      <select
        name="preferred_contact_method"
        value={form.preferred_contact_method}
        onChange={handleChange}
        className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-base md:col-span-2 md:text-sm"
      >
        <option value="">Preferred Contact Method</option>
        <option value="Email">Email</option>
        <option value="Phone">Phone</option>
        <option value="WhatsApp">WhatsApp</option>
      </select>

      {/* Status */}
      <select
        name="status"
        value={form.status}
        onChange={handleChange}
        className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-base md:col-span-2 md:text-sm"
      >
        <option value="Active">Active</option>
        <option value="Inactive">Inactive</option>
      </select>

      {/* Flags */}
      <div className="grid grid-cols-1 gap-2 md:col-span-2 md:grid-cols-2 md:gap-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <label className="flex min-h-[44px] items-center gap-3 rounded-md border border-gray-200 px-3 py-2 md:min-h-0 md:border-0 md:px-0 md:py-0">
            <input type="checkbox" name="portal_access" checked={form.portal_access} onChange={handleChange} className="h-5 w-5 shrink-0 md:h-4 md:w-4" />
            Portal Access
          </label>
          <label className="flex min-h-[44px] items-center gap-3 rounded-md border border-gray-200 px-3 py-2 md:min-h-0 md:border-0 md:px-0 md:py-0">
            <input type="checkbox" name="is_primary" checked={form.is_primary} onChange={handleChange} className="h-5 w-5 shrink-0 md:h-4 md:w-4" />
            Primary Contact
          </label>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <label className="flex min-h-[44px] items-center gap-3 rounded-md border border-gray-200 px-3 py-2 md:min-h-0 md:border-0 md:px-0 md:py-0">
            <input type="checkbox" name="is_billing_contact" checked={form.is_billing_contact} onChange={handleChange} className="h-5 w-5 shrink-0 md:h-4 md:w-4" />
            Billing Contact
          </label>
          <label className="flex min-h-[44px] items-center gap-3 rounded-md border border-gray-200 px-3 py-2 md:min-h-0 md:border-0 md:px-0 md:py-0">
            <input type="checkbox" name="is_technical_contact" checked={form.is_technical_contact} onChange={handleChange} className="h-5 w-5 shrink-0 md:h-4 md:w-4" />
            Technical Contact
          </label>
        </div>
      </div>

      {/* Notes */}
      <textarea
        name="notes"
        value={form.notes}
        onChange={handleChange}
        placeholder="Notes"
        className="min-h-[96px] w-full rounded-md border border-gray-300 px-3 py-2.5 text-base md:col-span-2 md:text-sm"
      />

      {/* Actions */}
      <div className="mt-2 flex w-full flex-col gap-3 md:col-span-2 md:mt-4 md:flex-row md:justify-between md:gap-4">
        <button
          type="submit"
          disabled={submitting}
          className="min-h-[44px] w-full rounded bg-btnActionNormal px-4 py-2 font-body font-semibold text-white shadow transition-colors duration-200 ease-in-out hover:bg-btnActionHover active:bg-btnActionClick md:w-auto"
          style={{ cursor: submitting ? "not-allowed" : "pointer" }}
        >
          {submitting ? "Saving..." : isEdit ? "Update Contact" : "Save Contact"}
        </button>
      </div>

      {message && <div className="mt-2 break-words text-sm text-gray-700 md:col-span-2">{message}</div>}
    </form>
  );
};

export default ContactFormWidget;
